/*
 * Project: bellcloud
 * File: apps/frontend/src/connections/pollStreamQueue.ts
 * Description: Class 16 버스트 스트림 패킷 폴링 (Serial/BLE/시뮬 공용).
 *   큐를 폴링하되, 링버퍼가 활발히 수신 중이면 인내심을 늘리고
 *   3초간 버퍼 변화도 큐도 없으면 조기 타임아웃으로 판단한다.
 */

import type { AsyncQueue } from './asyncQueue';

interface PollStreamQueueOptions {
  queue: AsyncQueue<Uint8Array>;
  /** 현재 링버퍼에 남은 바이트 수 */
  bufferAvailable: () => number;
  timeoutMs: number;
  /** 로그 접두 (예: '[Serial]') */
  logPrefix: string;
  pollIntervalMs?: number;
}

export async function pollStreamQueue({
  queue,
  bufferAvailable,
  timeoutMs,
  logPrefix,
  pollIntervalMs = 100,
}: PollStreamQueueOptions): Promise<Uint8Array> {
  const startTime = Date.now();
  let lastBufferSize = bufferAvailable();
  let lastBufferChangeTime = Date.now();
  let checkCount = 0;

  while (Date.now() - startTime < timeoutMs) {
    if (queue.size > 0) {
      try {
        return await queue.shift(pollIntervalMs);
      } catch {
        // 폴링 도중 큐가 비면 계속 시도
      }
    }

    checkCount++;
    const currentBufferSize = bufferAvailable();
    if (currentBufferSize !== lastBufferSize) {
      lastBufferSize = currentBufferSize;
      lastBufferChangeTime = Date.now();
    }

    const timeSinceBufferChange = Date.now() - lastBufferChangeTime;
    if (timeSinceBufferChange > 3000 && currentBufferSize === 0 && queue.size === 0) {
      console.error(
        `${logPrefix} readStreamPacket: timeout (${timeoutMs}ms, checked ${checkCount} times), ` +
          `no buffer change for ${timeSinceBufferChange}ms`
      );
      throw new Error(`Timeout: No stream packet received after ${timeoutMs}ms`);
    }

    await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
  }

  throw new Error(
    `Timeout: No stream packet received after ${timeoutMs}ms (checked ${checkCount} times)`
  );
}
